
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User, AuthError } from "@supabase/supabase-js";
import { useToast } from "@/components/ui/use-toast";

interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  company?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{
    success: boolean;
    message?: string;
  }>;
  signUp: (data: SignUpData) => Promise<{
    success: boolean;
    message?: string;
  }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getAuthErrorMessage = (error: AuthError): string => {
  switch (error.message) {
    case "Invalid login credentials":
      return "Email ou mot de passe incorrect";
    case "Email not confirmed":
      return "Veuillez confirmer votre email avant de vous connecter";
    case "User not found":
      return "Aucun compte trouvé avec cet email";
    case "Invalid email":
      return "Format d'email invalide";
    default:
      console.error("Unexpected auth error:", error);
      return "Une erreur est survenue lors de la connexion";
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    console.log("AuthProvider: Initializing auth state");
    
    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log("AuthProvider: Initial session check", { userId: session?.user?.id });
        setUser(session?.user ?? null);
      } catch (error) {
        console.error("AuthProvider: Error getting initial session", error);
      } finally {
        setLoading(false);
      }
    };

    initSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("AuthProvider: Auth state changed", { event, userId: session?.user?.id });
      
      setUser(session?.user ?? null);
      
      switch (event) {
        case 'SIGNED_OUT':
          console.log("AuthProvider: User signed out, redirecting to account");
          setUser(null);
          navigate('/account');
          break;
        // Ne plus rediriger automatiquement vers la page d'accueil lors de la connexion
        case 'SIGNED_IN':
          console.log("AuthProvider: User signed in");
          break;
        default:
          break;
      }
    });

    return () => {
      console.log("AuthProvider: Cleaning up auth subscriptions");
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signIn = async (email: string, password: string) => {
    console.log("AuthProvider: Attempting sign in", { email });
    
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("AuthProvider: Sign in error", error);
        return { 
          success: false, 
          message: getAuthErrorMessage(error)
        };
      }

      if (!data?.user) {
        console.error("AuthProvider: No user data received");
        return { 
          success: false, 
          message: "Erreur de connexion" 
        };
      }

      console.log("AuthProvider: Sign in successful", { userId: data.user.id });
      
      // La redirection sera gérée par onAuthStateChange
      return { success: true };
    } catch (error: any) {
      console.error("AuthProvider: Sign in error", error);
      return { 
        success: false, 
        message: "Une erreur est survenue" 
      };
    }
  };

  const signUp = async (signUpData: SignUpData) => {
    console.log("AuthProvider: Attempting sign up", { email: signUpData.email });
    
    try {
      const { error, data } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          data: {
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            phone: signUpData.phone,
            street_address: signUpData.streetAddress,
            city: signUpData.city,
            province: signUpData.province,
            postal_code: signUpData.postalCode,
            company: signUpData.company
          }
        }
      });

      if (error) {
        console.error("AuthProvider: Sign up error", error);
        return {
          success: false,
          message: getAuthErrorMessage(error)
        };
      }

      console.log("AuthProvider: Sign up successful", { userId: data.user?.id });
      
      toast({
        title: "Compte créé avec succès",
        description: "Veuillez vérifier votre email pour confirmer votre compte.",
      });

      return { success: true };
    } catch (error: any) {
      console.error("AuthProvider: Sign up error", error);
      return { 
        success: false, 
        message: error.message || "Une erreur est survenue lors de l'inscription" 
      };
    }
  };

  const signOut = async () => {
    console.log("AuthProvider: Attempting sign out");
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("AuthProvider: Sign out error", error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de la déconnexion",
          variant: "destructive",
        });
      } else {
        console.log("AuthProvider: Sign out successful");
        // La navigation sera gérée par onAuthStateChange
      }
    } catch (error) {
      console.error("AuthProvider: Sign out error", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la déconnexion",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
